/**
 * Live user-annotation sync across the tabs of this browser (PR #973 re-review).
 *
 * Every view keeps its own annotation list — the Analysis viewer's
 * `localAnnotations`, the Preview's `userAnnotations` — so an annotation written
 * in one tab reached another only on reload. With the #970 deep links it is easy
 * to keep the same analysis open in two tabs, which made that visible.
 *
 * `BrainFocusApi` announces each write the server accepted as a per-annotation
 * DELTA, and each view applies it to its own list by id. Deltas rather than
 * whole-list snapshots: a tab holding a stale list would otherwise erase the
 * newer annotations of every other tab. A `BroadcastChannel` never delivers a
 * message to the object that posted it, and applying a delta publishes nothing,
 * so there is no echo.
 *
 * Detached windows need nothing of their own: they are portals of the main
 * window's tree (`components/popout/popout_frame.tsx`). Other browsers, devices
 * and users see the change on their next load, as before.
 */

import { useEffect } from 'preact/hooks';
import deepEqual from 'fast-deep-equal';
import type { UserAnnotation } from '@brainfocus/web/domain/annotations/user_annotation';
import { BroadcastPort, openBroadcastPort, setBroadcastFactoryForTests } from '@brainfocus/web/shared/platform/broadcast';

/**
 * The slice of `BroadcastChannel` used here, so tests can supply a synchronous
 * double; opened through the shared factory (`shared/platform/broadcast.ts`).
 * Absence only loses the live push, never the write.
 */
export type AnnotationChannel = BroadcastPort;

/** A write to one annotation of recording `edfId`, as the server accepted it. */
export type AnnotationDelta =
    | { edfId: number; op: 'upsert'; annotation: UserAnnotation }
    | { edfId: number; op: 'delete'; annotationId: number };

const CHANNEL_NAME = 'bf-user-annotations';

let channel: AnnotationChannel | null | undefined;
const listeners = new Set<(delta: AnnotationDelta) => void>();

/** The channel, opened on first use so a page that never annotates opens none. */
function getChannel(): AnnotationChannel | null {
    if (channel === undefined) {
        channel = openBroadcastPort(CHANNEL_NAME);
        if (channel) {
            channel.onMessage = (data: unknown) => {
                if (!isDelta(data)) return;
                listeners.forEach(listener => listener(data));
            };
        }
    }
    return channel;
}

function isDelta(data: unknown): data is AnnotationDelta {
    if (typeof data !== 'object' || data === null) return false;
    const value = data as { edfId?: unknown; op?: unknown; annotation?: unknown; annotationId?: unknown };
    if (typeof value.edfId !== 'number') return false;
    if (value.op === 'upsert') {
        const annotation = value.annotation as { id?: unknown } | null | undefined;
        return typeof annotation === 'object' && annotation !== null && typeof annotation.id === 'number';
    }
    return value.op === 'delete' && typeof value.annotationId === 'number';
}

/** Tell the other tabs of this browser about a write the server accepted. */
export function publishAnnotationDelta(delta: AnnotationDelta): void {
    getChannel()?.postMessage(delta);
}

/**
 * Receive the deltas other tabs publish, for every recording.
 *
 * @returns A function that stops the delivery.
 */
export function subscribeAnnotationDeltas(listener: (delta: AnnotationDelta) => void): () => void {
    getChannel();
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
}

/**
 * Apply one delta to an annotation list: an upsert replaces the annotation with
 * the same id in place, or appends it; a delete drops it. Returns `list` itself
 * when nothing changes, so a state setter fed with it does not re-render.
 */
export function applyAnnotationDelta(list: UserAnnotation[], delta: AnnotationDelta): UserAnnotation[] {
    if (delta.op === 'delete') {
        return list.some(a => a.id === delta.annotationId)
            ? list.filter(a => a.id !== delta.annotationId)
            : list;
    }
    const incoming = delta.annotation;
    const index = list.findIndex(a => a.id === incoming.id);
    if (index === -1) return [...list, incoming];
    if (deepEqual(list[index], incoming)) return list;
    const next = list.slice();
    next[index] = incoming;
    return next;
}

/**
 * Keep an annotation list state in step with the writes other tabs make to
 * recording `edfId`. Nothing is listened to while `edfId` is undefined (a
 * session-only preview).
 *
 * @param edfId Recording whose annotations the list holds.
 * @param setAnnotations The list's state setter.
 */
export function useAnnotationBroadcast(
    edfId: number | undefined,
    setAnnotations: (update: (prev: UserAnnotation[]) => UserAnnotation[]) => void,
): void {
    useEffect(() => {
        if (edfId === undefined) return undefined;
        return subscribeAnnotationDeltas((delta) => {
            if (delta.edfId !== edfId) return;
            setAnnotations(prev => applyAnnotationDelta(prev, delta));
        });
    }, [edfId, setAnnotations]);
}

/**
 * Close the channel, drop every listener and, when given, install `factory` as the
 * shared broadcast factory for this channel (every other channel gets none). Test
 * seam only — never call from app code. The real `BroadcastChannel` must never be
 * opened by the suite: under jsdom it is Node's implementation, which keeps the
 * event loop alive and would hang Mocha (`global_setup.ts` installs a null factory).
 */
export function resetAnnotationBroadcastForTests(factory?: () => AnnotationChannel | null): void {
    channel?.close();
    channel = undefined;
    listeners.clear();
    if (factory) setBroadcastFactoryForTests(name => (name === CHANNEL_NAME ? factory() : null));
}
